import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';

const PieChart = ({ paymentAmounts, settlementBalance, payments }) => {
  const [chartData, setChartData] = useState({
    series: [],
    options: {
      chart: {
        fontFamily: 'Inter, sans-serif',
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          speed: 500,
          animateGradually: {
            enabled: true,
            delay: 150
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        }
      },
      labels: [],
      colors: ['#38bdf8', '#4ade80', '#f87171', '#fb923c', '#a78bfa', '#f472b6', '#60a5fa'],
      dataLabels: {
        enabled: true,
        formatter: function(val, opts) {
          return `${Math.round(val)}%`;
        },
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
          colors: ['#fff']
        },
        dropShadow: {
          enabled: true,
          blur: 2,
          opacity: 0.3
        }
      },
      plotOptions: {
        pie: {
          expandOnClick: true,
          offsetY: 0,
          donut: {
            size: '60%',
            background: 'transparent',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '16px',
                fontWeight: 600
              },
              value: {
                show: true,
                fontSize: '20px',
                fontWeight: 700,
                formatter: function(val) {
                  return '€' + Intl.NumberFormat('en-US').format(val);
                }
              },
              total: {
                show: true,
                showAlways: true,
                label: 'Total',
                fontSize: '16px',
                fontWeight: 600,
                formatter: function(w) {
                  const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                  return '€' + Intl.NumberFormat('en-US').format(total);
                }
              }
            }
          }
        }
      },
      tooltip: {
        enabled: true,
        theme: 'light',
        style: {
          fontSize: '14px'
        },
        y: {
          formatter: function(val) {
            return '€' + Intl.NumberFormat('en-US').format(val);
          }
        }
      },
      legend: {
        position: 'right',
        offsetY: 50,
        height: 300,
        fontSize: '14px',
        fontWeight: 500,
        markers: {
          width: 12,
          height: 12,
          radius: 6
        },
        itemMargin: {
          horizontal: 8,
          vertical: 5
        },
        formatter: function(seriesName, opts) {
          return seriesName;
        }
      },
      responsive: [
        {
          breakpoint: 1024,
          options: {
            legend: {
              position: 'bottom',
              offsetY: 0,
              height: 'auto'
            }
          }
        },
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 300
            },
            legend: {
              position: 'bottom',
              offsetY: 0,
              height: 'auto',
              fontSize: '12px'
            }
          }
        }
      ],
      states: {
        hover: {
          filter: {
            type: 'darken',
            value: 0.15
          }
        },
        active: {
          filter: {
            type: 'darken',
            value: 0.15
          }
        }
      }
    }
  });

  useEffect(() => {
    if (!payments || !paymentAmounts || !settlementBalance) return;

    const totalPaid = paymentAmounts.reduce((acc, curr) => acc + curr, 0);
    const series = [...paymentAmounts];

    const paymentTypeCount = {};
    
    const labels = payments.map((payment, index) => {
      const amount = paymentAmounts[index];
      const percentage = ((amount / settlementBalance) * 100).toFixed(1);
      
      if (!paymentTypeCount[payment.type]) {
        paymentTypeCount[payment.type] = 1;
      } else {
        paymentTypeCount[payment.type]++;
      }
      
      return `${payment.type} ${paymentTypeCount[payment.type]} - €${Intl.NumberFormat('en-US').format(amount)} (${percentage}%)`;
    });

    const colors = payments.map((payment) => {
      if (payment.cashed) {
        return '#10b981';
      }
      const today = new Date();
      const bookingDate = new Date(payment.booking_date);
      if (bookingDate < today) {
        return '#ef4444';
      }
      return '#94a3b8';
    });

    setChartData({
      ...chartData,
      series: series,
      options: {
        ...chartData.options,
        labels: labels,
        colors: colors
      }
    });
  }, [paymentAmounts, settlementBalance, payments]);

  const containerStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    position: 'relative'
  };

  const chartStyle = {
    flex: '1 1 auto',
    maxWidth: '60%'
  };

  const dividerStyle = {
    width: '1px',
    height: '300px',
    backgroundColor: '#e2e8f0',
    margin: '0 15px',
    boxShadow: '0 0 8px rgba(0, 0, 0, 0.05)'
  };

  const legendContainerStyle = {
    flex: '1 1 auto',
    maxWidth: '35%',
    padding: '0 0 0 15px'
  };

  const shouldRenderDivider = () => {
    return window.innerWidth >= 1024;
  };

  return (
    <div style={containerStyle}>
      <div style={chartStyle}>
        <Chart
          options={{
            ...chartData.options,
            legend: {
              ...chartData.options.legend,
              show: !shouldRenderDivider()
            }
          }}
          series={chartData.series}
          type="donut"
          width="100%"
          height="380"
        />
      </div>
      
      {shouldRenderDivider() && (
        <>
          <div style={dividerStyle}></div>
          
          <div style={legendContainerStyle}>
            
              
              {chartData.options.labels.map((label, index) => (
                <div 
                  key={index} 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '10px',
                    padding: '8px',
                    borderRadius: '4px',
                    backgroundColor: 'transparent'
                  }}
                >
                  <span 
                    style={{
                      display: 'inline-block',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: chartData.options.colors[index],
                      marginRight: '10px'
                    }}
                  ></span>
                  <span style={{ fontSize: '14px' }}>{label}</span>
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PieChart;